import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import {
  useTheme,
  Box,
  Typography,
  Chip,
  Tooltip,
  Stack,
  MenuItem,
  TextField,
  CircularProgress,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
} from '@mui/material';
import {
  AddCircleOutline,
  Delete,
  WarningAmber,
  NavigateBefore,
  NavigateNext,
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import {
  useGetSeasonsQuery,
  useGetScheduledFlightsQuery,
  useGetAircraftRotationsQuery,
  useCreateAircraftRotationMutation,
  useUpdateAircraftRotationMutation,
  useDeleteAircraftRotationsMutation,
  useAddRotationLegMutation,
  useRemoveRotationLegMutation,
  useValidateRotationMutation,
  useGetActiveAcTypesQuery,
} from 'features/apiSlice';
import { LAYOUT_HEIGHT } from 'state/constants';
import { 
  buildPermissionMap, 
  hasRight,
} from 'state/checkPermission';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import FlexBetween from 'components/FlexBetween';
import DeleteConfirm from 'components/DeleteConfirm';
import {
  DAY_LABELS,
  LABEL_W,
  RULER_H,
  RULER_TOP,
  MIN_ZOOM,
  MAX_ZOOM,
  AC_COLORS,
  DEF_ROW_H,
} from './constants';
import WeekDayRuler from './WeekDayRuler';
import WeekRotationRow from './WeekRotationRow';
import DetailPanel from './DetailPanel';
import RotationFormDialog from './RotationFormDialog';
import AddLegDialog from './AddLegDialog';


const RotationBuilder = () => {
  useAuthCheck();
  const user  = useSelector((state) => state.user);
  const theme = useTheme();

  const permissionMap = useMemo(
    () => buildPermissionMap(user?.permissions),
    [user?.permissions]
  );
  const canCreate = hasRight(permissionMap, "aircraftrotations", "create", false);
  const canUpdate = hasRight(permissionMap, "aircraftrotations", "update", false);
  const canDelete = hasRight(permissionMap, "aircraftrotations", "delete", false);

  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [filterAcType,     setFilterAcType]     = useState('');
  const [filterDay,        setFilterDay]        = useState('ALL');
  const [selectedId,       setSelectedId]       = useState(null);
  const [zoom,             setZoom]             = useState(1);
  const rowHeight = DEF_ROW_H;
  const [paginationModel,  setPaginationModel]  = useState({ page: 0, pageSize: 50 });
  const [formDialog,       setFormDialog]       = useState({ open: false, data: null });
  const [legDialog,        setLegDialog]        = useState({ open: false, rotationId: null, rotationCode: '' });
  const [confirmDelete,    setConfirmDelete]    = useState(false);
  const [legSearch,        setLegSearch]        = useState('');
  const [legHighlight,     setLegHighlight]     = useState(0);

  const scrollRef   = useRef(null);
  const [containerW, setContainerW] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setContainerW(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const dayW   = Math.max(containerW - LABEL_W, 1) / 7 * zoom;
  const totalW = dayW * 7;

  /* ── Data ──────────────────────────────────────────────── */
  const { data: seasonsData } = useGetSeasonsQuery({ limit: 200 });
  const seasons = useMemo(() => seasonsData?.data || [], [seasonsData]);

  useEffect(() => {
    if (!selectedSeasonId && seasons.length > 0) {
      const active = seasons.find((s) => s.status === 'active') || seasons[0];
      if (active) setSelectedSeasonId(active._id);
    }
  }, [seasons, selectedSeasonId]);

  const rotationParams = useMemo(() => ({
    seasonId: selectedSeasonId,
    acType: filterAcType || undefined,
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
  }), [selectedSeasonId, filterAcType, paginationModel]);

  const { data: rotData, isLoading, refetch } = useGetAircraftRotationsQuery(rotationParams, { skip: !selectedSeasonId });
  const rotations = useMemo(() => rotData?.data || [], [rotData]);
  const total     = rotData?.total || 0;

  const { data: acTypesData } = useGetActiveAcTypesQuery();
  const acTypeOptions = useMemo(() => acTypesData || [], [acTypesData]);

  const { data: fltData } = useGetScheduledFlightsQuery(
    { seasonId: selectedSeasonId, limit: 500 },
    { skip: !selectedSeasonId || !legDialog.open }
  );
  const allFlights = useMemo(() => fltData?.data || [], [fltData]);

  const selectedRotation = useMemo(() => rotations.find((r) => r._id === selectedId), [rotations, selectedId]);

  const acTypeColorMap = useMemo(() => {
    const types = [...new Set(rotations.map((r) => r.acType).filter(Boolean))];
    return Object.fromEntries(types.map((t, i) => [t, AC_COLORS[i % AC_COLORS.length]]));
  }, [rotations]);

  const [createRotation]   = useCreateAircraftRotationMutation();
  const [updateRotation]   = useUpdateAircraftRotationMutation();
  const [deleteRotations]  = useDeleteAircraftRotationsMutation();
  const [addLeg]           = useAddRotationLegMutation();
  const [removeLeg]        = useRemoveRotationLegMutation();
  const [validateRotation] = useValidateRotationMutation();

  /* ── Handlers ──────────────────────────────────────────── */
  const handleSave = useCallback(async (values, { setSubmitting, resetForm }) => {
    try {
      if (formDialog.data) {
        await updateRotation({ id: formDialog.data._id, ...values }).unwrap();
        toast.success('Rotation updated.');
      } else {
        await createRotation(values).unwrap();
        toast.success('Rotation created.');
      }
      resetForm();
      setFormDialog({ open: false, data: null });
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  }, [
    formDialog.data, 
    createRotation, 
    updateRotation, 
    refetch
  ]);

  const handleDelete = useCallback(async () => {
    if (!selectedId) return;
    try {
      await deleteRotations([selectedId]).unwrap();
      toast.success('Rotation deleted.');
      setSelectedId(null);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Delete failed.');
    }
  }, [
    selectedId, 
    deleteRotations, 
    refetch
  ]);

  const handleAddLeg = useCallback(async (scheduledFlightId) => {
    if (!legDialog.rotationId) return;
    try {
      await addLeg({ id: legDialog.rotationId, scheduledFlightId }).unwrap();
      toast.success('Leg added.');
      setLegSearch('');
      setLegHighlight(0);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed.');
    }
  }, [
    legDialog.rotationId, 
    addLeg, 
    refetch
  ]);

  const handleRemoveLeg = useCallback(async (rotationId, legIndex) => {
    try {
      await removeLeg({ id: rotationId, legIndex }).unwrap();
      toast.success('Leg removed.');
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || 'Failed.');
    }
  }, [removeLeg, refetch]);

  const handleValidate = useCallback(async (id) => {
    try {
      const result = await validateRotation({ id }).unwrap();
      if (result.isValid) toast.success('Rotation is valid ✓');
      else toast.error(`${result.validationIssues.length} issue(s) found.`);
      refetch();
    } catch {
      toast.error('Validation failed.');
    }
  }, [validateRotation, refetch]);


  const closeLegDialog = useCallback(() => {
    setLegDialog({ open: false, rotationId: null, rotationCode: '' });
    setLegSearch('');
  }, []);

  const closeFormDialog = useCallback(() => setFormDialog({ open: false, data: null }), []);

  const filteredFlights = useMemo(() => {
    const q = legSearch.toLowerCase();
    return allFlights.filter((f) =>
      !q ||
      `${f.carrier}${f.flightNo}`.toLowerCase().includes(q) ||
      f.dep?.toLowerCase().includes(q) ||
      f.arr?.toLowerCase().includes(q)
    );
  }, [allFlights, legSearch]);

  const formInitial = useMemo(() => formDialog.data ? {
    seasonId:     formDialog.data.seasonId || selectedSeasonId,
    rotationCode: formDialog.data.rotationCode || '',
    acType:       formDialog.data.acType || '',
    acReg:        formDialog.data.acReg || '',
    notes:        formDialog.data.notes || '',
  } : {
    seasonId:     selectedSeasonId,
    rotationCode: '',
    acType:       filterAcType || '',
    acReg:        '',
    notes:        '',
  }, [formDialog.data, selectedSeasonId, filterAcType]);

  const totalPages = Math.ceil(total / paginationModel.pageSize);

  const statsChips = useMemo(() => {
    const types  = [...new Set(rotations.map((r) => r.acType).filter(Boolean))];
    const issues = rotations.filter((r) => !r.isValid).length;
    const legs   = rotations.reduce((s, r) => s + (r.legs?.length || 0), 0);
    return { types, issues, legs };
  }, [rotations]);

  const scrollToMorning = () => { if (scrollRef.current) scrollRef.current.scrollLeft = (6 / 24) * dayW; };

  /* ── Keyboard shortcuts ────────────────────────────────── */
  useEffect(() => {
    const handleKey = (e) => {
      if (formDialog.open || legDialog.open || confirmDelete) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Escape') {
        setSelectedId(null);
      } else if (e.key === 'n' || e.key === 'N') {
        if (!selectedSeasonId || !canCreate) return;
        e.preventDefault();
        setFormDialog({ open: true, data: null });
      } else if (e.key === 'e' || e.key === 'E') {
        if (selectedId && canUpdate && selectedRotation) setFormDialog({ open: true, data: selectedRotation });
      } else if (e.key === 'Delete') {
        if (selectedId && canDelete) setConfirmDelete(true);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [
    formDialog.open, 
    legDialog.open, 
    confirmDelete, 
    selectedSeasonId, 
    canCreate, 
    canUpdate, 
    canDelete, 
    selectedId, 
    selectedRotation
  ]);

  
  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="Rotation Builder" 
          subtitle="Aircraft Rotation Board" 
        />
        <Stack direction="row" alignItems="center" spacing={1}>          
          <Chip variant="outlined" size="small" label={`${total} rotation${total !== 1 ? 's' : ''}`} />
          {statsChips.legs > 0   && <Chip label={`${statsChips.legs} legs`} size="small" variant="outlined" />}
          {statsChips.issues > 0 && <Chip label={`${statsChips.issues} issues`} size="small" color="warning" variant="outlined" icon={<WarningAmber />} />}

          <TextField
            select
            size="small"
            label="Season"
            value={selectedSeasonId}
            onChange={(e) => setSelectedSeasonId(e.target.value)}
            slotProps={{
              select: {
                renderValue: (selected) =>
                  seasons.find((s) => s._id === selected)?.seasonCode || '',
              },
            }}
          >
            {seasons.map((s) => (
              <MenuItem key={s._id} value={s._id}>{s.seasonCode} - {s.seasonName}</MenuItem>
            ))}
          </TextField>
          
          {selectedId && (
            <Tooltip title="Delete (Del)" arrow>
              <span>
                <StyledButton size="small" disabled={!canDelete} onClick={() => setConfirmDelete(true)}>
                  <Delete />
                </StyledButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="New Rotation (N)" arrow>
            <span>
              <StyledButton size="small" disabled={!selectedSeasonId} onClick={() => setFormDialog({ open: true, data: null })}>
                <AddCircleOutline />
              </StyledButton>
            </span>
          </Tooltip>
        </Stack>
      </FlexBetween>

      <Box 
        display="grid" 
        gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
        gap={2}
      >
        <Box sx={{ gridColumn: 'span 12', height: LAYOUT_HEIGHT, display: 'flex', flexDirection: 'column', gap: 1 }}>

          {/* Toolbar */}
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
            <TextField
              select
              size="small"
              label="AC Type"
              value={filterAcType}
              onChange={(e) => { 
                setFilterAcType(e.target.value); 
                setPaginationModel((p) => ({ ...p, page: 0 })); 
              }}
              sx={{ minWidth: 100 }}
            >
              <MenuItem value="">All</MenuItem>
              {acTypeOptions.map((t) => (
                <MenuItem 
                  key={t._id} 
                  value={t.acTypeCode}
                >
                  {t.acTypeCode}
                </MenuItem>
              ))}
            </TextField>

            <ToggleButtonGroup 
              exclusive 
              size="small" 
              value={filterDay} 
              onChange={(_, v) => v && setFilterDay(v)}
            >
              <ToggleButton 
                value="ALL" 
                sx={{ px: 1.2, fontSize: '0.68rem' }}
              >
                All
              </ToggleButton>
              {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                <ToggleButton 
                  key={d} 
                  value={String(d)} 
                  sx={{ px: 1, fontSize: '0.68rem' }}
                >
                  {DAY_LABELS[d].slice(0, 2)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {/* Zoom controls */}
            <Box display="flex" alignItems="center" gap={0.3}>
              <Tooltip title="Zoom out">
                <StyledButton 
                  size="small" 
                  onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - 0.15).toFixed(2)))}
                >
                  <ZoomOut fontSize="small" />
                </StyledButton>
              </Tooltip>

              <Typography 
                sx={{ 
                  minWidth: 36, 
                  textAlign: 'center', 
                  fontSize: '0.72rem', 
                  fontFamily: 'monospace' 
                }}
              >
                {Math.round(zoom * 100)}%
              </Typography>
              
              <Tooltip title="Zoom in">
                <StyledButton 
                  size="small" 
                  onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + 0.15).toFixed(2)))}
                >
                  <ZoomIn fontSize="small" />
                </StyledButton>
              </Tooltip>

              <Tooltip title="Scroll to Mon 06:00">
                <StyledButton 
                  size="small" 
                  onClick={scrollToMorning}
                >
                  <CenterFocusStrong fontSize="small" />
                </StyledButton>
              </Tooltip>
            </Box>

            {/* AC type legend / quick filter */}
            {statsChips.types.length > 0 && (
              <Box display="flex" gap={0.4} flexWrap="wrap">
                {statsChips.types.map((t) => {
                  const c      = acTypeColorMap[t] || '#607d8b';
                  const active = filterAcType === t;

                  return (
                    <Chip 
                      key={t} 
                      label={t} 
                      size="small"
                      onClick={() => setFilterAcType(active ? '' : t)}
                      sx={{
                        bgcolor: active ? c : `${c}55`,
                        color: active ? '#fff' : 'text.primary',
                        border: active ? `2px solid ${c}` : '1px solid transparent',
                        fontSize: '0.62rem', 
                        cursor: 'pointer', 
                        transition: 'all 0.15s',
                      }} />
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Board + Detail — vertical resizable split */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%', 
                gap: selectedRotation ? 0.5 : 0 
              }}
            >
              {/* ── Rotation board — always fills remaining height ── */}
              <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                <Paper
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                  }}
                >
                  <Box 
                    ref={scrollRef} 
                    sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
                  >
                    {/* Sticky week ruler */}
                    <Box 
                      sx={{
                        zIndex: 10,
                        position: 'sticky', 
                        top: 0, 
                        display: 'flex', 
                        flexShrink: 0,
                        minWidth: LABEL_W + totalW,
                        bgcolor: 'background.alt',
                        borderBottom: (t) => `2px solid ${t.palette.divider}`,
                      }}
                    >
                      <Box 
                        sx={{
                          zIndex: 11, 
                          position: 'sticky', 
                          left: 0,
                          height: RULER_H, 
                          width: LABEL_W,
                          display: 'flex', 
                          flexDirection: 'column', 
                          flexShrink: 0,
                          bgcolor: 'background.alt',
                          borderRight: (t) => `1px solid ${t.palette.divider}`,
                        }}
                      >
                        <Box 
                          sx={{
                            height: RULER_TOP, 
                            display: 'flex', 
                            alignItems: 'center', 
                            px: 1.2,
                            borderBottom: (t) => `1px solid ${t.palette.divider}`,
                          }}
                        >
                          <Typography 
                            sx={{ 
                              fontSize: '0.7rem', 
                              color: 'text.disabled', 
                              fontFamily: 'monospace', 
                              letterSpacing: 1.5, 
                              userSelect: 'none' 
                            }}
                          >
                            WEEK VIEW
                          </Typography>
                        </Box>
                        <Box 
                          sx={{ 
                            flex: 1, 
                            display: 'flex', 
                            alignItems: 'center', 
                            px: 1.2 
                          }}
                        >
                          <Typography 
                            sx={{ 
                              fontSize: '0.7rem', 
                              color: 'text.secondary', 
                              fontFamily: 'monospace', 
                              letterSpacing: 1.2, 
                              textTransform: 'uppercase', 
                              userSelect: 'none' 
                            }}
                          >
                            Rotation
                          </Typography>
                        </Box>
                      </Box>
                      <WeekDayRuler dayW={dayW} />
                    </Box>

                    {/* Rotation rows */}
                    {isLoading ? (
                      <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                      </Box>
                    ) : rotations.length === 0 ? (
                      <Box 
                        textAlign="center" 
                        p={4} 
                        sx={{ minWidth: LABEL_W + totalW }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {selectedSeasonId ? 'No rotations found. Press N to create one.' : 'Select a season to view rotations.'}
                        </Typography>
                      </Box>
                    ) : (
                      rotations.map((rot) => (
                        <WeekRotationRow
                          key={rot._id}
                          rotation={rot}
                          isSelected={selectedId === rot._id}
                          theme={theme}
                          dayW={dayW}
                          barColor={acTypeColorMap[rot.acType] || '#607d8b'}
                          rowHeight={rowHeight}
                          onClick={() => setSelectedId(rot._id === selectedId ? null : rot._id)}
                        />
                      ))
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <FlexBetween 
                        sx={{
                          zIndex: 4, 
                          position: 'sticky', 
                          bottom: 0, 
                          minWidth: LABEL_W + totalW,
                          py: 0.5, 
                          gap: 1, 
                          borderTop: `1px solid ${theme.palette.divider}`,
                          bgcolor: 'background.paper',
                        }}
                      >
                        <IconButton size="small" disabled={paginationModel.page === 0}
                          onClick={() => setPaginationModel((p) => ({ ...p, page: p.page - 1 }))}>
                          <NavigateBefore fontSize="small" />
                        </IconButton>
                        <Typography variant="caption" color="text.secondary">
                          Page {paginationModel.page + 1} / {totalPages} · {total} total
                        </Typography>
                        <IconButton size="small" disabled={paginationModel.page + 1 >= totalPages}
                          onClick={() => setPaginationModel((p) => ({ ...p, page: p.page + 1 }))}>
                          <NavigateNext fontSize="small" />
                        </IconButton>
                      </FlexBetween>
                    )}
                  </Box>
                </Paper>

                {/* Hint overlay — no layout space taken */}
                {!selectedRotation && (
                  <Box 
                    sx={{
                      zIndex: 5, 
                      position: 'absolute', 
                      bottom: 12, 
                      left: '50%',
                      transform: 'translateX(-50%)',
                      pointerEvents: 'none',
                    }}
                  >
                    <Chip
                      size="small"
                      label="Click a rotation to view its details below"
                      sx={{ opacity: 0.5, fontSize: '0.65rem', bgcolor: 'background.paper' }}
                    />
                  </Box>
                )}
              </Box>

              {/* ── Detail panel — only rendered when a rotation is selected ── */}
              {selectedRotation && (
                <Box sx={{ height: 220, flexShrink: 0 }}>
                  <DetailPanel
                    rotation={selectedRotation}
                    theme={theme}
                    onRemoveLeg={handleRemoveLeg}
                    onAddLeg={() => { setLegDialog({ open: true, rotationId: selectedRotation._id, rotationCode: selectedRotation.rotationCode }); setLegHighlight(0); }}
                    onValidate={() => handleValidate(selectedRotation._id)}
                    onEdit={() => setFormDialog({ open: true, data: selectedRotation })}
                    canUpdate={canUpdate}
                  />
                </Box>
              )}

            </Box>
          </Box>
        </Box>
      </Box>

      <RotationFormDialog
        open={formDialog.open}
        onClose={closeFormDialog}
        initialValues={formInitial}
        data={formDialog.data}
        seasons={seasons}
        onSave={handleSave}
      />

      <AddLegDialog
        open={legDialog.open}
        onClose={closeLegDialog}
        rotationCode={legDialog.rotationCode}
        flights={filteredFlights}
        search={legSearch}
        onSearchChange={setLegSearch}
        highlight={legHighlight}
        onHighlightChange={setLegHighlight}
        onAddLeg={handleAddLeg}
      />

      <DeleteConfirm 
        open={confirmDelete} 
        onClose={() => setConfirmDelete(false)} 
        onConfirm={handleDelete} 
        deleteType="single" 
      />
    </Box>
  );
};

export default RotationBuilder;
